import { useState, useEffect, useRef } from 'react';
import { useToast } from '../../context/ToastProvider.js';
import config from '../../constants/config.js';
import './../../styles/Modal.css';
import fallback from './../../assets/fallback-logo.png';

function GemstoneLinkerModal({
    onClose,
    onUpdate
}) {
    const addToast = useToast();
    const validViews = [
        "MANAGE",
        "CONNECT",
    ];

    const [ gemstoneLinks , setGemstoneLinks ] = useState( {} );
    const [ loadingLinks , setLoadingLinks ] = useState( false );
    const [ currentView , setCurrentView ] = useState( validViews[0] );
    const [ selectedGemstoneLinkIndex , setSelectedGemstoneLinkIndex ] = useState( -1 );
    const [ refreshLinks , setRefreshLinks ] = useState( 0 );
    const [ uploading , setUploading ] = useState( false );
    const [ removing , setRemoving ] = useState( false );

    const [ selectedProductId, setSelectedProductId ] = useState( '-1' );
    const [ gemstoneProducts , setGemstoneProducts ] = useState( [] );
    const [ loadingGemstoneProducts , setLoadingGemstoneProducts ] = useState( false );
    const [ selectedRadioOption , setSelectedRadioOption ] = useState( '' );

    useEffect( () => {
        async function load() {
            setLoadingLinks( true );

            const _gemstoneProductLinks = await getGemstoneProductLinks();
            if ( _gemstoneProductLinks != null ) {
                setGemstoneLinks({ ..._gemstoneProductLinks })
            }

            setLoadingLinks( false );
        }

        load();
    } , [ refreshLinks ] );

    // USE ONLY WHEN A VIEW CHANGES
    useEffect( () => {
        if ( currentView === validViews[0] ) {
            setGemstoneLinks({});
            setSelectedGemstoneLinkIndex( -1 );
            setSelectedProductId( '-1' );
            setGemstoneProducts( [] );
            setRefreshLinks( refreshLinks + 1 );
        }

        if ( currentView === validViews[1] ) {
            async function load() {
                setLoadingGemstoneProducts( true );

                const storeDetails = gemstoneLinks.shopsWithoutLinkedGemstoneProduct[ selectedGemstoneLinkIndex ].store_details;
                const _gemstoneProducts = await getGemstoneProducts( storeDetails.shop );
                if ( _gemstoneProducts.length > 0 ) {
                    setGemstoneProducts( _gemstoneProducts );
                    setSelectedProductId( _gemstoneProducts[0].id );
                    setSelectedRadioOption( "existing" );
                } else {
                    setGemstoneProducts( [] );
                    setSelectedProductId( '-1' );
                    setSelectedRadioOption( "new" );
                }

                setLoadingGemstoneProducts( false );
            }

            load();
        }
    } , [ currentView ] );

    function formatTimeAgo( timestamp ) {
        const now = new Date();
        const lastModified = new Date(timestamp);
        const diffInSeconds = Math.floor((now - lastModified) / 1000);
    
        if (diffInSeconds < 60) {
            return `${diffInSeconds} sec(s) ago`;
        } else if (diffInSeconds < 3600) {
            return `${Math.floor(diffInSeconds / 60)} min(s) ago`;
        } else if (diffInSeconds < 86400) {
            return `${Math.floor(diffInSeconds / 3600)} hour(s) ago`;
        } else if (diffInSeconds < 432000) {
            return `${Math.floor(diffInSeconds / 86400)} day(s) ago`;
        } else {
            return lastModified.toLocaleDateString();
        }
    }

    async function getGemstoneProductLinks() {
        let gemstoneLinks = null;

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/links/gemstones/products`;
            const requestSettings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data?.links != null ) {
                gemstoneLinks = data.links;
            }
        } catch ( error ) {
            console.log( error );
        }

        return gemstoneLinks;
    }

    async function getGemstoneProducts( shop ) {
        let gemstoneProducts = [];

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/links/gemstones/products/${encodeURIComponent( shop )}`;
            const requestSettings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data?.products?.length > 0 ) {
                gemstoneProducts = data.products;
            }
        } catch ( error ) {
            console.log( error );
        }

        return gemstoneProducts;
    }

    async function createLink() {
        setLoadingLinks( true );
        setUploading( true );
        
        try {
            const storeDetails = gemstoneLinks.shopsWithoutLinkedGemstoneProduct[ selectedGemstoneLinkIndex ].store_details;
            const shop = storeDetails.shop;

            const payload = {
                shop: shop,
                action: selectedRadioOption,
                gemstone_product_id: selectedProductId
            };

            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/links/gemstones/products`;
            const requestSettings = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify( payload )
            };

            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data?.errors?.length > 0 ) {
                addToast( `Failed to link gemstone - ${data?.errors[0].message}`, true , 5000 );
            } else {
                addToast( `Linked Gemstone to Product!`, false , 5000 );
            }
        } catch ( error ) {
            console.log( error );
        }

        if ( typeof onUpdate === 'function' ) {
            onUpdate();
        }

        setCurrentView( validViews[0] );
        setRefreshLinks( refreshLinks + 1 );
        setLoadingLinks( false );
        setUploading( false );
    }

    async function removeLink( shop ) {
        setRemoving( true );

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/links/gemstones/products/${encodeURIComponent( shop )}`;
            const requestSettings = {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                }
            };

            const response = await fetch( url , requestSettings );
            const data = await response.json();
            if ( data?.errors?.length > 0 ) {
                addToast( `Failed to remove gemstone link - ${data?.errors[0].message}`, true , 5000 );
            } else {
                addToast( `Removed Gemstone Product Link!`, false , 5000 );
            }
        } catch ( error ) {
            console.log( error );
        }

        if ( typeof onUpdate === 'function' ) {
            onUpdate();
        }

        setRemoving( false );
        setRefreshLinks( refreshLinks + 1 );
    }

    return( 
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content wide" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>
                    <span>×</span>
                    <span className="sr-only">Close modal</span>
                </button>

                { currentView === validViews[0] && (
                    <div>
                        <h1>Gemstone Product Links</h1>

                        <h2 className="section-title">Manage Links</h2>
                        <p className="section-description">Remove existing gemstone-product links</p>

                        { loadingLinks && (
                            <div style={{ maxHeight: 150 }} className="product-table-skeleton"></div>
                        )}

                        { gemstoneLinks?.shopsWithLinkedGemstoneProduct?.length > 0 && !loadingLinks && (
                            <table className="existing-gemstone-links">
                                <thead>
                                    <tr>
                                        <th>Shop</th>
                                        <th>Product</th>
                                        <th>Linked on</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    { gemstoneLinks.shopsWithLinkedGemstoneProduct.map( ( linked , index ) => {
                                        return(
                                            <tr key={index}>
                                                <td>
                                                    <div>
                                                        { linked.store_details.nickname }
                                                    </div>
                                                </td>

                                                <td>
                                                    <div>
                                                        { linked.product_gemstone_link.title }
                                                    </div>
                                                </td>

                                                <td>
                                                    <div>
                                                        { formatTimeAgo( linked?.product_gemstone_link?.linked_on ) }
                                                    </div>
                                                </td>

                                                <td>
                                                    <div>
                                                        <button disabled={removing} onClick={() => { removeLink( linked.store_details.shop ) }}>
                                                            { removing ?
                                                                <svg width="15" height="15" viewBox="0 0 50 50" className="spinner">
                                                                    <circle cx="25" cy="25" r="20" stroke="white" strokeWidth="5" fill="none" strokeDasharray="90 150" strokeLinecap="round">
                                                                        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                                                                    </circle>
                                                                </svg>
                                                                :
                                                                <span>Remove</span>
                                                            }
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                                
                            </table>
                        )}

                        { gemstoneLinks?.shopsWithLinkedGemstoneProduct?.length <= 0 && !loadingLinks && (
                            <div className="no-linked">
                                No shops have products linked! ⚠️
                            </div>
                        )}

                        <h2 className="section-title">Create new Links</h2>
                        <p className="section-description">Add new gemstone-product links.</p>

                        { loadingLinks && (
                            <div className="new-gemstone-links">
                                <div className="link-wrapper skeleton">
                                    <div className="logo">
                                        <div className="skeleton-box circle"></div>
                                    </div>
                                    <div className="info">
                                        <div className="skeleton-box text-line short"></div>
                                        <div className="skeleton-box text-line long"></div>
                                    </div>
                                    <div className="actions">
                                        <div className="skeleton-box button"></div>
                                    </div>
                                </div>

                                <div className="link-wrapper skeleton">
                                    <div className="logo">
                                        <div className="skeleton-box circle"></div>
                                    </div>
                                    <div className="info">
                                        <div className="skeleton-box text-line short"></div>
                                        <div className="skeleton-box text-line long"></div>
                                    </div>
                                    <div className="actions">
                                        <div className="skeleton-box button"></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        { gemstoneLinks?.shopsWithoutLinkedGemstoneProduct?.length > 0 && !loadingLinks && (
                            <div className="new-gemstone-links">
                                { gemstoneLinks.shopsWithoutLinkedGemstoneProduct.map( ( unlinked , index ) => {
                                    return(
                                        <div className="link-wrapper" key={index}>
                                            <div className='logo'>
                                                <img 
                                                    className="logo"
                                                    src={ unlinked?.store_details?.logo_url || fallback }
                                                    alt="site-logo"
                                                    loading="lazy"
                                                    width={50}
                                                    height={50}
                                                />
                                            </div>
                                            <div className="info">
                                                <span className="store-nickname">{ unlinked?.store_details?.nickname || unlinked?.store_details?.shop }</span>
                                                <span className="description">Connect a gemstone product for the current shop</span>
                                            </div>
                                            <div className="actions">
                                                <button onClick={ () => {
                                                    setCurrentView( validViews[1] );
                                                    setSelectedGemstoneLinkIndex( index );
                                                }} className="connect">
                                                    Connect
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                        
                        { gemstoneLinks?.shopsWithoutLinkedGemstoneProduct?.length <= 0 && !loadingLinks && (
                            <div className="all-linked">
                                All shops have a linked product! 🎉
                            </div>
                        )}
                    </div>
                )}
                
                {currentView === validViews[1] && (
                    <div className="link-gemstone-view">
                        <div className="view-header">
                            <button className="back" onClick={() => {
                                setCurrentView( validViews[0] )
                            }}>
                                <svg width={20} height={20} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"/></svg>
                            </button>
                            <h1>Link Gemstone Product</h1>
                        </div>

                        <div className="form-group">
                            <div className="inline-radio">
                                <input
                                    type="radio"
                                    id="existing"
                                    name="link_mode"
                                    value="existing"
                                    onChange={ () => {
                                        setSelectedRadioOption( "existing" );
                                    }}
                                    disabled={ gemstoneProducts.length <= 0 || loadingGemstoneProducts }
                                    checked={ selectedRadioOption === "existing" }
                                />
                                <label htmlFor="existing">Select from existing products</label>
                            </div>
                            
                            <select 
                                id="select-gemstone-product"
                                value={selectedProductId} 
                                disabled={ gemstoneProducts.length <= 0 || loadingGemstoneProducts }
                                onChange={ ( e ) => { 
                                    setSelectedProductId( e.target.value ) 
                                }}
                            >
                                <option value="-1" disabled={true}>Select a product</option>
                                { gemstoneProducts.map( ( product , index ) => {
                                    return(
                                        <option key={product.id} value={product.id}>
                                            { product.title }
                                        </option>
                                    )
                                })}
                                
                                
                            </select>

                            <div className="inline-radio">
                                <input
                                    type="radio"
                                    id="new"
                                    name="link_mode"
                                    value="new"
                                    disabled={ loadingGemstoneProducts }
                                    onChange={ () => {
                                        setSelectedRadioOption( "new" );
                                    }}
                                    checked={ selectedRadioOption === "new" }
                                />
                                <label htmlFor="new">Or create a new product</label>
                            </div>
                        
                            <button 
                                disabled={ loadingGemstoneProducts || uploading }
                                className="create-link"
                                onClick={createLink}
                            >
                                { uploading ?
                                    <svg width="15" height="15" viewBox="0 0 50 50" className="spinner">
                                        <circle cx="25" cy="25" r="20" stroke="white" strokeWidth="5" fill="none" strokeDasharray="90 150" strokeLinecap="round">
                                            <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                                        </circle>
                                    </svg>
                                    :
                                    <span>CREATE PRODUCT LINK</span>
                                }
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div> 
    );
}

export default GemstoneLinkerModal;